/**
 * Capability-based access helper.
 *
 * Roles (Django groups) grant capabilities (codenames). The logged-in user's
 * payload carries `capabilities` (union across their roles). Gate UI on these
 * instead of magic group ids.
 *
 * Backward-compat: if `capabilities` is missing (older/cached session, or the
 * backend hasn't been deployed yet), fall back to the legacy group-id mapping
 * so existing behaviour is preserved.
 */

// Legacy group id -> capabilities, used only as a fallback.
const LEGACY_GROUP_CAPS = {
  1: ["view_data", "add_data", "edit_data", "validate_data", "manage_buildings",
      "manage_kaya", "manage_members", "manage_dictionary", "manage_boundaries",
      "manage_users", "manage_roles"], // Administrator
  2: ["view_data", "add_data", "edit_data", "manage_kaya", "manage_members"], // Locality
  3: ["view_data"], // Normal
  4: ["view_data", "add_data", "edit_data"], // District
  5: ["view_data", "add_data", "edit_data"], // Region
};

function legacyCaps(user) {
  const set = new Set();
  (user?.groups || []).forEach((g) => {
    // groups may be ids or objects {id, name}
    const id = typeof g === "object" ? g.id : g;
    (LEGACY_GROUP_CAPS[id] || []).forEach((c) => set.add(c));
  });
  return set;
}

/**
 * Does the user have a capability?
 * Staff/superuser implicitly have everything.
 */
export function can(user, capability) {
  if (!user) return false;
  if (user.is_staff || user.is_superuser) return true;
  if (Array.isArray(user.capabilities)) {
    return user.capabilities.includes(capability);
  }
  return legacyCaps(user).has(capability);
}

/** True if the user has ANY of the given capabilities. */
export function canAny(user, capabilities = []) {
  return capabilities.some((c) => can(user, c));
}
